import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  Download,
  Trash2,
  Play,
  RefreshCw,
  HardDrive,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  BookOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { GlassCard, PageLoading, SectionHeader } from '../components/shared';
import { audioStorage } from '../audio/audioStorage';
import { audioDownload } from '../audio/audioDownload';
import { isNative } from '../audio/nativeFileStorage';
import { useAudio } from '../context/AudioContext';
import { quranApi } from '../services/api';
import { toast } from 'sonner';

const fmtMB = (b) => b ? `${(b / (1024 * 1024)).toFixed(1)} Mo` : '—';

const ScopeRow = ({ scope, surahMap, onPlay, onDelete, onResume, t }) => {
  const STATUS_LABEL = {
    completed:   { tone: 'text-emerald-400', icon: CheckCircle2, label: t('audio_dl.status_completed') },
    partial:     { tone: 'text-amber-400',   icon: AlertTriangle, label: t('audio_dl.status_partial') },
    failed:      { tone: 'text-red-400',     icon: AlertTriangle, label: t('audio_dl.status_failed') },
    downloading: { tone: 'text-primary',     icon: Loader2,       label: t('audio_dl.status_downloading') },
    pending:     { tone: 'text-text-muted',  icon: Loader2,       label: t('audio_dl.status_pending') },
  };
  const meta = STATUS_LABEL[scope.status] || STATUS_LABEL.pending;
  const Icon = meta.icon;

  let title = '';
  if (scope.scopeType === 'surah') {
    const sname = surahMap?.[scope.surahNumber];
    title = t('audio_dl.scope_surah', { n: scope.surahNumber, name: sname ? ' — ' + sname : '' });
  } else if (scope.scopeType === 'hizb') {
    title = t('audio_dl.scope_hizb', { n: scope.hizbNumber });
  } else {
    title = t('audio_dl.scope_full');
  }

  return (
    <div className="flex items-center gap-3 p-3 border border-border rounded-lg bg-surface-elevated/40"
         data-testid={`audio-scope-row-${scope.id}`}>
      <div className="w-9 h-9 rounded-md bg-black/30 flex items-center justify-center">
        <BookOpen className="w-4 h-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary truncate">{title}</p>
        <div className="flex items-center gap-2 text-[11px] text-text-muted mt-0.5">
          <Icon className={`w-3 h-3 ${meta.tone} ${scope.status === 'downloading' ? 'animate-spin' : ''}`} />
          <span className={meta.tone}>{meta.label}</span>
          <span>· {t('audio_dl.verses_count_inline', { count: scope.ayahKeys?.length || 0 })}</span>
          {scope.sizeBytes && <span>· {fmtMB(scope.sizeBytes)}</span>}
          <span>· {scope.reciterId}</span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {scope.status === 'completed' && (
          <Button size="sm" variant="ghost" onClick={() => onPlay(scope)}
                  data-testid={`audio-scope-play-${scope.id}`}>
            <Play className="w-4 h-4" />
          </Button>
        )}
        {(scope.status === 'partial' || scope.status === 'failed') && (
          <Button size="sm" variant="outline" onClick={() => onResume(scope)}
                  data-testid={`audio-scope-resume-${scope.id}`}>
            <RefreshCw className="w-4 h-4 mr-1" /> {t('audio_dl.resume')}
          </Button>
        )}
        <Button size="sm" variant="ghost" className="text-red-300"
                onClick={() => onDelete(scope)}
                data-testid={`audio-scope-delete-${scope.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const AudioDownloads = () => {
  const { t } = useTranslation();
  const { playSurah, playHizb, playFullQuran, state, reciters, setReciter, setAudioMode } = useAudio();
  const [scopes, setScopes] = useState(null);
  const [storage, setStorage] = useState(null);
  const [surahMap, setSurahMap] = useState({});
  const [persistInfo, setPersistInfo] = useState({ native: false, persisted: null });

  const refresh = useCallback(async () => {
    const [s, st, surahs, persistedFlag] = await Promise.all([
      audioStorage.getDownloadedScopes(),
      audioStorage.getStorageUsage(),
      quranApi.getSurahs().then((r) => r.data).catch(() => []),
      // Web only: tells us if the browser granted persistent storage.
      (typeof navigator !== 'undefined' && navigator.storage?.persisted)
        ? navigator.storage.persisted().catch(() => null)
        : Promise.resolve(null),
    ]);
    setScopes(s.sort((a, b) => (b.downloadedAt || '').localeCompare(a.downloadedAt || '')));
    setStorage(st);
    setPersistInfo({ native: isNative(), persisted: persistedFlag });
    const map = {};
    surahs.forEach((su) => { map[su.number] = su.name_french || su.name_english; });
    setSurahMap(map);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handlePlay = async (scope) => {
    if (scope.scopeType === 'surah') await playSurah(scope.surahNumber);
    else if (scope.scopeType === 'hizb') await playHizb(scope.hizbNumber);
    else await playFullQuran();
  };

  const handleDelete = async (scope) => {
    if (!window.confirm(t('audio_dl.delete_confirm'))) return;
    try {
      if (!scope.id) {
        toast.error('Impossible de supprimer: identifiant manquant');
        return;
      }
      await audioStorage.deleteScope(scope.id);
      toast.success(t('audio_dl.deleted'));
      refresh();
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      toast.error(err?.message || t('audio_dl.delete_failed') || 'Erreur lors de la suppression');
    }
  };

  const handleResume = async (scope) => {
    await audioDownload.resumeScope(scope);
    toast.message(t('audio_dl.download_resumed'));
    setTimeout(refresh, 1500);
  };

  if (!scopes) return <PageLoading />;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="audio-downloads-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/settings" className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" /> {t('audio_dl.back_to_settings')}
        </Link>
        <SectionHeader
          overline={t('audio_dl.overline')}
          title={t('audio_dl.title')}
          subtitle={t('audio_dl.subtitle')}
        />

        {/* Persistence status badge */}
        <div
          className="mt-4 mb-2 flex items-center gap-2 text-xs"
          data-testid="audio-persistence-status"
        >
          {persistInfo.native ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-text-secondary">
                Stockage natif (Capacitor Filesystem) — vos téléchargements survivent au redémarrage de l'appareil.
              </span>
            </>
          ) : persistInfo.persisted === true ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-text-secondary">
                Stockage persistant accordé par le navigateur — vos téléchargements ne seront pas effacés automatiquement.
              </span>
            </>
          ) : persistInfo.persisted === false ? (
            <>
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-text-secondary">
                Stockage non persistant — le navigateur peut effacer les fichiers sous pression mémoire.
                Pour une persistance garantie, installez l'application mobile.
              </span>
            </>
          ) : (
            <>
              <HardDrive className="w-4 h-4 text-text-muted" />
              <span className="text-text-muted">État de persistance inconnu (ce navigateur ne supporte pas l'API).</span>
            </>
          )}
        </div>

        <GlassCard className="my-6">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-text-muted mb-1">{t('audio_dl.reciter')}</p>
              <select
                value={state.reciterId}
                onChange={(e) => setReciter(e.target.value)}
                className="w-full h-9 px-2 rounded-md bg-surface border border-border text-sm"
                data-testid="audio-prefs-reciter"
              >
                {reciters.map((r) => (<option key={r.id} value={r.id}>{r.label}</option>))}
              </select>
              <p className="text-[10px] text-text-muted mt-1">{t('audio_dl.riwaya_label', { r: state.riwaya })}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-text-muted mb-1">{t('audio_dl.audio_mode')}</p>
              <select
                value={state.audioMode}
                onChange={(e) => setAudioMode(e.target.value)}
                className="w-full h-9 px-2 rounded-md bg-surface border border-border text-sm"
                data-testid="audio-prefs-mode"
              >
                <option value="offline_only">{t('audio_dl.mode_offline')}</option>
                <option value="offline_first_with_streaming_fallback">{t('audio_dl.mode_hybrid')}</option>
              </select>
              <p className="text-[10px] text-text-muted mt-1 flex items-center gap-1">
                <HardDrive className="w-3 h-3" />
                {storage?.usedBytes ? t('audio_dl.used', { n: fmtMB(storage.usedBytes) }) : t('audio_dl.storage')}
                {storage?.availableBytes && ` · ${t('audio_dl.free', { n: fmtMB(storage.availableBytes) })}`}
              </p>
            </div>
          </div>
        </GlassCard>

        {scopes.length === 0 ? (
          <GlassCard className="text-center py-10">
            <Download className="w-8 h-8 mx-auto text-text-muted mb-3" />
            <p className="text-sm text-text-secondary">
              {t('audio_dl.empty_title')}
            </p>
            <p className="text-xs text-text-muted mt-1">
              {t('audio_dl.empty_desc')}
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-2">
            {scopes.map((s) => (
              <ScopeRow key={s.id} scope={s} surahMap={surahMap} t={t}
                        onPlay={handlePlay} onDelete={handleDelete} onResume={handleResume} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioDownloads;
